import { Eye, FileDown, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Loader } from "@/components/common/loader";
import {
  createLetter,
  generateLetter,
  previewLetter,
  resolveMergeFields,
} from "@/features/letters/api";
import type {
  Addressee,
  LetterTemplate,
  MergeField,
} from "@/features/letters/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: LetterTemplate;
  clientId: string;
  clientMarried: boolean;
  /** Called after a letter is generated so the page can refresh its list. */
  onGenerated: () => void;
  /** Open the generated PDF (shared with the page's document opener). */
  onOpenDocument: (documentId: string) => void;
}

type Step = "fill" | "review" | "save";

const ADDRESSEE_OPTIONS: { value: Addressee; label: string }[] = [
  { value: "T", label: "Taxpayer" },
  { value: "S", label: "Spouse" },
  { value: "J", label: "Jointly" },
];

const MULTILINE_TYPES = new Set(["M", "B", "L"]);

/** Substitute tokens into a tags-stripped body for a plain-text review preview.
 * Display-only (never injected as HTML), so it needs no sanitizing. */
function mergePreviewText(
  body: string,
  system: Record<string, string>,
  fieldValues: Record<string, string>,
): string {
  const substituted = body.replace(
    /\{([a-z0-9_]+)(?::[A-Za-z]!?)?\}/gi,
    (_m, name: string) => {
      if (name in fieldValues) {
        return (fieldValues[name] || "")
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean)
          .join(", ");
      }
      return system[name] ?? "";
    },
  );
  return substituted
    .replace(/<[^>]+>/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();
}

export function LetterWizardDialog({
  open,
  onOpenChange,
  template,
  clientId,
  clientMarried,
  onGenerated,
  onOpenDocument,
}: Props) {
  const [step, setStep] = useState<Step>("fill");
  const [addressee, setAddressee] = useState<Addressee>("T");
  const [fields, setFields] = useState<MergeField[]>([]);
  const [system, setSystem] = useState<Record<string, string>>({});
  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadingFields, setLoadingFields] = useState(false);
  const [busy, setBusy] = useState(false);
  const [docName, setDocName] = useState("");
  // Reuse one draft letter across preview/generate rather than creating many.
  const draftIdRef = useRef<string | null>(null);

  // Reset everything each time the dialog opens for a template.
  useEffect(() => {
    if (!open) return;
    setStep("fill");
    setAddressee("T");
    setValues({});
    setErrors({});
    setDocName(`Letter - ${template.name}`);
    draftIdRef.current = null;
  }, [open, template.id, template.name]);

  // Changing the addressee changes what the letter says, so drop any draft
  // created under the previous addressee — the next preview/generate re-creates
  // it so the stored draft never drifts from what's shown.
  useEffect(() => {
    draftIdRef.current = null;
  }, [addressee]);

  // Resolve fill-in fields + system token values (re-run when addressee changes,
  // since the system values differ per taxpayer/spouse/joint).
  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoadingFields(true);
    resolveMergeFields(template.id, clientId, addressee)
      .then((res) => {
        if (!active) return;
        setFields(res.fields);
        setSystem(res.system);
      })
      .catch(() => active && toast.error("Couldn't load the template's fields."))
      .finally(() => active && setLoadingFields(false));
    return () => {
      active = false;
    };
  }, [open, template.id, clientId, addressee]);

  const previewText = useMemo(
    () => mergePreviewText(template.body, system, values),
    [template.body, system, values],
  );

  function setValue(name: string, value: string) {
    setValues((v) => ({ ...v, [name]: value }));
    if (errors[name]) setErrors((e) => ({ ...e, [name]: "" }));
  }

  function validateFill(): boolean {
    const next: Record<string, string> = {};
    for (const f of fields) {
      if (f.required && !(values[f.name] || "").trim()) {
        next[f.name] = "This field is required.";
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  const ensureDraft = useCallback(async (): Promise<string> => {
    if (draftIdRef.current) return draftIdRef.current;
    const letter = await createLetter(clientId, template.id, addressee);
    draftIdRef.current = letter.id;
    return letter.id;
  }, [clientId, template.id, addressee]);

  async function handlePreview() {
    setBusy(true);
    try {
      const id = await ensureDraft();
      const blob = await previewLetter(id, { fields: values, addressee });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      // Give the new tab time to load, then release the blob (matches openDocument).
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error("Couldn't render the preview.");
    } finally {
      setBusy(false);
    }
  }

  async function handleGenerate() {
    if (!docName.trim()) {
      toast.error("Enter a document name.");
      return;
    }
    setBusy(true);
    let letter;
    try {
      const id = await ensureDraft();
      letter = await generateLetter(id, {
        fields: values,
        addressee,
        documentName: docName.trim(),
      });
    } catch {
      setBusy(false);
      toast.error("Couldn't generate the letter.");
      return;
    }
    setBusy(false);
    toast.success("Letter generated");
    onGenerated();
    onOpenChange(false);
    if (letter.document) onOpenDocument(letter.document.id);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{template.name}</DialogTitle>
          <DialogDescription>
            {step === "fill" && "Fill in the letter's blanks, then continue."}
            {step === "review" && "Review the merged letter and preview the PDF."}
            {step === "save" && "Name the document saved to the client's Letters folder."}
          </DialogDescription>
        </DialogHeader>

        {loadingFields ? (
          <div className="flex justify-center py-8">
            <Loader />
          </div>
        ) : step === "fill" ? (
          <div className="flex flex-col gap-4">
            {clientMarried && (
              <div className="grid gap-2">
                <Label>Addressed to</Label>
                <RadioGroup
                  value={addressee}
                  onValueChange={(v) => setAddressee(v as Addressee)}
                  className="flex gap-4"
                >
                  {ADDRESSEE_OPTIONS.map((o) => (
                    <label
                      key={o.value}
                      className="flex items-center gap-2 text-sm"
                      htmlFor={`addr-${o.value}`}
                    >
                      <RadioGroupItem id={`addr-${o.value}`} value={o.value} />
                      {o.label}
                    </label>
                  ))}
                </RadioGroup>
              </div>
            )}

            {fields.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                This template has no fill-in fields — all its content is filled from
                the client's data. Continue to preview and generate.
              </p>
            ) : (
              fields.map((f) => (
                <div key={f.name} className="grid gap-2">
                  <Label htmlFor={`field-${f.name}`}>
                    {f.label}
                    {f.required && <span className="text-destructive"> *</span>}
                    {(f.type === "B" || f.type === "L") && (
                      <span className="text-muted-foreground ml-2 text-xs font-normal">
                        one item per line
                      </span>
                    )}
                  </Label>
                  {MULTILINE_TYPES.has(f.type) ? (
                    <Textarea
                      id={`field-${f.name}`}
                      rows={f.type === "M" ? 4 : 3}
                      value={values[f.name] ?? ""}
                      onChange={(e) => setValue(f.name, e.target.value)}
                    />
                  ) : (
                    <Input
                      id={`field-${f.name}`}
                      type={f.type === "D" ? "date" : "text"}
                      inputMode={f.type === "N" ? "decimal" : undefined}
                      placeholder={
                        f.type === "N"
                          ? "1,234.56"
                          : f.type === "P"
                            ? "(512) 555-1212"
                            : undefined
                      }
                      value={values[f.name] ?? ""}
                      onChange={(e) => setValue(f.name, e.target.value)}
                    />
                  )}
                  {errors[f.name] && (
                    <p className="text-destructive text-xs">{errors[f.name]}</p>
                  )}
                </div>
              ))
            )}
          </div>
        ) : step === "review" ? (
          <div className="flex flex-col gap-3">
            <div className="bg-muted/40 max-h-72 overflow-y-auto rounded-md border p-4 text-sm whitespace-pre-wrap">
              {previewText || "(empty)"}
            </div>
            <p className="text-muted-foreground text-xs">
              This is a quick text preview. Use “Preview PDF” to see the letterhead,
              fonts and formatting exactly as the saved PDF will look.
            </p>
            <div>
              <Button variant="outline" size="sm" onClick={handlePreview} disabled={busy}>
                <Eye /> Preview PDF
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="grid gap-2">
              <Label htmlFor="doc-name">Document name</Label>
              <Input
                id="doc-name"
                value={docName}
                onChange={(e) => setDocName(e.target.value)}
                maxLength={200}
              />
              <p className="text-muted-foreground text-xs">
                Saves to the client's <strong>Letters</strong> folder. A same-named
                letter is kept as a new version. Use ASCII characters only.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === "fill" && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (validateFill()) setStep("review");
                }}
                disabled={loadingFields}
              >
                Continue
              </Button>
            </>
          )}
          {step === "review" && (
            <>
              <Button variant="outline" onClick={() => setStep("fill")}>
                Back
              </Button>
              <Button onClick={() => setStep("save")}>Save to Documents…</Button>
            </>
          )}
          {step === "save" && (
            <>
              <Button variant="outline" onClick={() => setStep("review")} disabled={busy}>
                Back
              </Button>
              <Button onClick={handleGenerate} disabled={busy || !docName.trim()}>
                {busy ? <Loader2 className="animate-spin" /> : <FileDown />}
                Generate PDF
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
